// src/pages/ReportItem.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../firebase"; // compat
import { getFunctions, httpsCallable } from "firebase/functions";
import "./LegalPages.css";

const MAX_LEN = 2000;

function normalizeFunctionsCode(code) {
  // Firebase callable errors often look like: "functions/already-exists"
  const c = String(code || "").toLowerCase().trim();
  if (!c) return "";
  return c.includes("/") ? c.split("/").pop() : c;
}

export default function ReportItem() {
  const { tradeId } = useParams();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;

  // Cookie mode still counts as authed for viewing, but callable needs Firebase auth.
  const isAuthed = !!(currentUser && (currentUser.uid || currentUser._source === "cookie"));

  const functions = useMemo(() => getFunctions(), []);
  const reportTrade = useMemo(() => httpsCallable(functions, "reportTrade"), [functions]);

  const [tradeTitle, setTradeTitle] = useState("");
  const [tradeOwnerId, setTradeOwnerId] = useState("");
  const [tradeLoadErr, setTradeLoadErr] = useState("");

  const [reason, setReason] = useState("suspected_stolen");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const remaining = MAX_LEN - (details?.length || 0);

  const isSelfReport = useMemo(() => {
    if (!uid) return false;
    return !!tradeOwnerId && tradeOwnerId === uid;
  }, [uid, tradeOwnerId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setTradeLoadErr("");
      setTradeTitle("");
      setTradeOwnerId("");

      try {
        if (!tradeId) return;

        const snap = await db.collection("Trades").doc(tradeId).get();
        if (!snap.exists) {
          if (!cancelled) setTradeLoadErr("This trade could not be found.");
          return;
        }

        const data = snap.data() || {};
        const title = data.title || "Trade";
        const owner = String(data.ownerId || data.ownerUid || "").trim();

        if (!cancelled) {
          setTradeTitle(title);
          setTradeOwnerId(owner);
        }
      } catch {
        if (!cancelled) setTradeLoadErr("Could not load trade details. Please try again.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tradeId]);

  const canSubmit =
    isAuthed &&
    !!tradeId &&
    reason &&
    details.trim().length >= 10 &&
    details.length <= MAX_LEN &&
    !sending &&
    !isSelfReport;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");

    if (!isAuthed) {
      setErr("Please sign in to submit a report.");
      return;
    }

    if (isSelfReport) {
      setErr("You can’t report your own listing.");
      return;
    }

    if (!tradeId) {
      setErr("Missing trade reference. Please open the report page again from the trade.");
      return;
    }

    if (details.trim().length < 10) {
      setErr("Please add a short description (at least 10 characters) before submitting.");
      return;
    }

    setSending(true);
    try {
      const payload = {
        tradeId,
        reason,
        details: details.trim(),
      };

      const res = await reportTrade(payload);
      const id = res?.data?.reportId;
      const decision = res?.data?.decision; // logged | flagged | held

      const msg =
        decision === "held"
          ? "Report submitted. This listing has been placed on hold for review."
          : decision === "flagged"
          ? "Report submitted. This listing has been flagged for staff review."
          : "Report submitted. Thank you — our team will review it.";

      setOk(id ? `${msg} Reference: ${id}` : msg);

      setReason("suspected_stolen");
      setDetails("");
    } catch (e2) {
      const rawCode = e2?.code || e2?.details?.code;
      const code = normalizeFunctionsCode(rawCode);

      if (code === "already-exists") {
        setErr("You already reported this item. Thank you — our team will review it.");
      } else if (code === "failed-precondition") {
        setErr("You can’t report your own listing.");
      } else if (code === "resource-exhausted") {
        setErr("You’ve reached the report limit for today. Please try again tomorrow.");
      } else if (code === "not-found") {
        setErr("This trade could not be found. It may have been removed.");
      } else if (code === "unauthenticated") {
        setErr("Please sign in again to submit a report.");
      } else if (code === "invalid-argument") {
        setErr(e2?.message || "Please check the form and try again.");
      } else {
        setErr(e2?.message || "Could not submit the report right now. Please try again shortly.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    
       <div className="legal-page" style={{ paddingTop: 24, marginTop: 0 }}>
      <div className="legal-hero">
        <h1>Report an item</h1>
        <p>
          Reporting helps NexterZone detect fraud, stolen goods, and unsafe activity. Reports are reviewed by staff.
        </p>
        <div className="legal-hero-links">
          <Link to="/help">Help Center</Link>
          <Link to="/trust-safety">Trust &amp; Safety</Link>
        </div>
      </div>

      <div className="legal-callout">
        <h3>Reporting: {tradeTitle || "Trade"}</h3>
        <p className="legal-muted" style={{ marginTop: 6 }}>
          Trade ID: <strong>{tradeId}</strong>
        </p>
        {tradeLoadErr ? <p style={{ marginTop: 6 }}>{tradeLoadErr}</p> : null}
      </div>

      {!isAuthed ? (
        <div className="legal-callout">
          <h3>Sign in required</h3>
          <p>To prevent abuse and protect the community, reporting requires an authenticated account.</p>
          <div className="legal-actions">
            <Link className="legal-btn primary" to="/login" state={{ from: `/report/${tradeId}` }}>
              Sign in
            </Link>
            <Link className="legal-btn" to="/help">
              Back to Help Center
            </Link>
          </div>
        </div>
      ) : (
        <div className="legal-grid">
          <section className="legal-card" style={{ gridColumn: "span 12" }}>
            <h2>Submit a report</h2>
            <p className="legal-muted" style={{ marginTop: 0 }}>
              For immediate danger or confirmed theft, contact local law enforcement first.
            </p>

            {isSelfReport ? (
              <div className="legal-callout">
                <h3>Not allowed</h3>
                <p>You can’t report your own listing. If you need help, contact support.</p>
                <div className="legal-actions">
                  <Link className="legal-btn" to="/help">
                    Help Center
                  </Link>
                  <Link className="legal-btn" to={`/trade/${tradeId}`}>
                    Back to trade
                  </Link>
                </div>
              </div>
            ) : null}

            <form onSubmit={onSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 12 }}>
                <div style={{ gridColumn: "span 12" }}>
                  <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>Reason</label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #d1d5db" }}
                    disabled={sending || isSelfReport}
                  >
                    <option value="suspected_stolen">Suspected stolen item</option>
                    <option value="fraud_or_scam">Fraud / scam behaviour</option>
                    <option value="counterfeit">Counterfeit or fake item</option>
                    <option value="misleading_listing">Misleading listing / wrong condition</option>
                    <option value="prohibited_item">Prohibited or restricted item</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{ gridColumn: "span 12" }}>
                  <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>Details</label>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Explain what you noticed and why it seems suspicious."
                    style={{
                      width: "100%",
                      padding: 10,
                      borderRadius: 10,
                      border: "1px solid #d1d5db",
                      minHeight: 150,
                    }}
                    maxLength={MAX_LEN}
                    disabled={sending || isSelfReport}
                  />
                  <div className="legal-muted" style={{ marginTop: 6 }}>
                    {remaining} characters remaining
                  </div>
                  <div className="legal-muted" style={{ marginTop: 6 }}>
                    Please do not include phone numbers, addresses, or personal contact details.
                  </div>
                </div>
              </div>

              {err ? (
                <div className="legal-callout">
                  <h3>Could not submit</h3>
                  <p>{err}</p>
                </div>
              ) : null}

              {ok ? (
                <div className="legal-callout">
                  <h3>Submitted</h3>
                  <p>{ok}</p>
                </div>
              ) : null}

              <div className="legal-actions">
                <button
                  type="submit"
                  className="legal-btn primary"
                  disabled={!canSubmit}
                  style={{ cursor: canSubmit ? "pointer" : "not-allowed" }}
                >
                  {sending ? "Submitting..." : "Submit report"}
                </button>

                <Link className="legal-btn" to={`/trade/${tradeId}`}>
                  Back to trade
                </Link>

                <Link className="legal-btn" to="/help">
                  Help Center
                </Link>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
